

import usePaymentsApiContext from '@contexts/paymentsApiContext'
import styled from '@emotion/styled'
import { CardElement, Elements, PaymentElement } from '@stripe/react-stripe-js'
import { colors, fontSize, size } from '@styles/theme'
import { useLayoutEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import SubscriptionConfirmationForm from './SubscriptionConfirmationForm'
import useLoading from '@hooks/useLoading'
import { PositionedTopProgress } from '@elements/Loaders'
import { PiArrowRightBold, PiSealCheckBold } from 'react-icons/pi'
import { ButtonBase } from '@mui/material'
import StyledAlert from '@elements/StyledAlert'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  height: 100%;
  width: 100%;
  padding-top: ${size(2)};

  overflow-x: hidden;
`

const SubscriptionErrorDisplay = styled.h2`
  text-align: center;
  padding: ${size(3)}
`

const PaymentSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: 100%;
  padding-bottom: ${size(15)};
`

const PaymentSuccessButton = styled(ButtonBase)`
  padding: ${size(2)} ${size(4)};
  border-radius: 100px;
  font-weight: 600;
  margin-top: ${size(2)};
  background-color: ${colors.success(500)};
`

const SubscriptionConfirmation = () => {
  const { isLoading: isConfirmationLoading, setIsLoading: setIsConfirmationLoading, isLoadingDelayed: isConfirmationLoadingDelayed } = useLoading()
  const { createSubscription, paymentsPromise, isCustomerCreated } = usePaymentsApiContext()
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false)
  const { isLoading, setIsLoading, isLoadingDelayed } = useLoading(false, 500)
  const [subscriptionError, setSubscriptionError] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan')

  // Creates subscription and sets client secret when component renders
  useLayoutEffect(() => {
    setIsLoading(true)
    if (paymentsPromise && isCustomerCreated) {
      createSubscription(plan)
        .then((res) => {
          setIsLoading(false)
          setClientSecret(res.data.clientSecret)
          setSubscription(res.data)
        })
        .catch((err) => {
          setIsLoading(false)
          console.log(err.response)
          if (err?.response?.data?.errors?.user === 'has active subscription') {
            setSubscriptionError('This user already has an active subscription.')
          }
        })
    }
  }, [paymentsPromise, isCustomerCreated])

  const renderLoader = () => {
    return <h2>Setting up payments...</h2>
  }

  const renderPaymentSuccess = () => (
    <PaymentSuccessContainer>
      <PiSealCheckBold fill={colors.success(500)} size={size(8)} />
      <h2>Payment Successful</h2>
      <p style={{ textAlign: 'center', marginTop: 0, lineHeight: size(3), padding: `0 ${size(2)}` }}>
        Enjoy the new features ❤️
        <br />
        If you have any questions, contact us at <a style={{ color: colors.success(500) }} href= "mailto: apollo10510@gmail.com">apollo10510@gmail.com</a>
      </p>
      <PaymentSuccessButton
        to='/map'
        component={Link}>
        Go Home&nbsp;
        <PiArrowRightBold />
      </PaymentSuccessButton>
    </PaymentSuccessContainer>
  )

  const renderForm = () => {
    if (paymentsPromise && isCustomerCreated && clientSecret) return (
      <>
        <h2>Confirm Subscription</h2>
        <br />
        <Elements stripe={paymentsPromise} options={{ clientSecret, appearance: { variables: { colorText: '#ffffff' } } }}>
          <SubscriptionConfirmationForm
            clientSecret={clientSecret}
            subscription={subscription}
            isLoading={isConfirmationLoading}
            setIsLoading={setIsConfirmationLoading}
            setIsPaymentSuccessful={setIsPaymentSuccessful}
          />
        </Elements>
      </>
    )
  }

  const renderSubscription = () => {
    if (isLoadingDelayed) return (
      renderLoader()
    ); else if (subscriptionError) return (
      <SubscriptionErrorDisplay>
        {subscriptionError}
      </SubscriptionErrorDisplay>
    ); else if (isPaymentSuccessful) return (
      renderPaymentSuccess()
    ); else return (
      renderForm()
    )
  }

  return (
    <>
      <PositionedTopProgress
        style={{ zIndex: 2 }}
        isLoadingDelayed={isConfirmationLoadingDelayed}
      />
      <Container>
        {renderSubscription()}
      </Container>
    </>
  )
}

export default SubscriptionConfirmation
